import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as appConstants from "../../../constants/index";
import SweetAlert from "react-bootstrap-sweetalert";
import { ProceedButton } from "../../common/login/loginTags";
import { setRetryPayment } from "src/services/CouponPayment";
import { DarkButton, LightButton } from "src/styled-components/customButtons";
import BookingSummarySkeleton from "src/skeletons/BookingSummarySkeleton";
import "./Upi.css";

const STATUS_INTERVAL = 6000;
const PAYMENT_TIMEOUT_SECONDS = 120;
const REDIRECT_DELAY_MS = 2500;
const ACTIVE_UPI_PAYMENT_KEY = "active_upi_payment";

const IOS_UPI_APPS = [
  { id: "phonepe", label: "PhonePe", scheme: "phonepe://pay" },
  { id: "gpay", label: "Google Pay", scheme: "tez://upi/pay" },
  { id: "paytm", label: "Paytm", scheme: "paytmmp://upi/pay" },
  { id: "cred", label: "Cred", scheme: "credpay://upi/pay" },
];

const SUCCESS_STATUSES = ["PAID", "CHARGED", "COD_INITIATED", "PARTIAL_CHARGED", "AUTHORIZED"];
const PENDING_STATUSES = ["NEW", "PENDING", "PENDING_VBV", "AUTHORIZING", "STARTED", "CAPTURE_INITIATED"];

const getBookType = () => {
  const t = localStorage.getItem("booking_type");
  if (t === "GIFTCARD") return "GIFTCARD";
  if (t === "FOOD") return "FOOD";
  return "BOOKING";
};

const getInitialAmount = () => {
  try {
    const g = JSON.parse(localStorage.getItem("grandTotalData") || "null");
    if (g?.finalPrice != null) {
      const v = Number(g.finalPrice);
      return Number.isNaN(v) ? null : v;
    }
    const gv = localStorage.getItem("grandVal");
    if (gv) {
      const v = Number(String(gv).replace(/,/g, ""));
      return Number.isNaN(v) ? null : v;
    }
  } catch {}
  return null;
};

const formatAmount = (value) => {
  const v = Number(value);
  return Number.isNaN(v) ? "--" : `₹${v.toFixed(2)}`;
};

const buildIosIntentUrl = (scheme, genericIntent) => {
  if (!genericIntent) return "";
  const qs = genericIntent.replace(/^upi:\/\/pay\?/, "");
  return qs ? `${scheme}?${qs}` : "";
};

const getStoredActivePayment = () => {
  try {
    const s = sessionStorage.getItem(ACTIVE_UPI_PAYMENT_KEY);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

const isExternalUrl = (url) => /^https?:\/\//i.test(url || "");

const ConfirmCancelModal = ({ open, loading, onClose, onConfirm }) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="upi-confirm-root">
      <div className="upi-confirm-backdrop" onClick={loading ? undefined : onClose} />
      <div className="upi-confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="upi-cancel-title">
        <div className="upi-confirm-title" id="upi-cancel-title">
          Are you sure you want to cancel?
        </div>
        <div className="upi-confirm-text">
          If you click Yes, your booking payment will be cancelled. If you click No, this popup will close.
        </div>
        <div className="upi-confirm-actions">
          <LightButton type="button" className="upi-confirm-no" onClick={onClose} disabled={loading}>
            No
          </LightButton>
          <DarkButton type="button" className="upi-confirm-yes" onClick={onConfirm} disabled={loading}>
            {loading ? "Cancelling..." : "Yes"}
          </DarkButton>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Upi = ({ promotionText, payMode }) => {
  const navigate = useNavigate();

  const bookingId = localStorage.getItem("bookingid");
  const transId = localStorage.getItem("transid");
  const theme = localStorage.getItem("theme");
  const token = sessionStorage.getItem("token") || "";
  const subscription = localStorage.getItem("subscription");
  const chain = theme === "inoxTheme" ? "INOX" : "PVR";
  const bookType = getBookType();
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  const headersRef = useRef({
    Authorization: `Bearer ${token}`,
    chain,
    city: localStorage.getItem("city"),
    appVersion: appConstants.av,
    platform: appConstants.pt,
    country: appConstants.cname,
  });

  const paymentIdRef = useRef("");
  const forwardUrlRef = useRef("");
  const pollTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const reloadCancelDoneRef = useRef(false);
  const inFlightRef = useRef(false);
  const finishedRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState(getInitialAmount);
  const [alertState, setAlertState] = useState({ show: false, title: "", message: "" });
  const [statusLabel, setStatusLabel] = useState(
    promotionText || "You will be redirected to your UPI app to complete payment."
  );
  const [genericIntentUrl, setGenericIntentUrl] = useState("");
  const [iosChooserVisible, setIosChooserVisible] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(null);
  const [timerExpired, setTimerExpired] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState(false);
  const [manualCanceling, setManualCanceling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [iosUpiApps, setIosUpiApps] = useState([]);

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const clearCountdownTimer = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  const clearActivePayment = () => {
    sessionStorage.removeItem(ACTIVE_UPI_PAYMENT_KEY);
  };

  const persistActivePayment = (details) => {
    sessionStorage.setItem(ACTIVE_UPI_PAYMENT_KEY, JSON.stringify(details));
  };

  const stopAll = useCallback(() => {
    clearPollTimer();
    clearCountdownTimer();
    inFlightRef.current = false;
  }, []);

  const resetFlow = useCallback(() => {
    stopAll();
    finishedRef.current = false;
    paymentIdRef.current = "";
    forwardUrlRef.current = "";
    setLoading(false);
    setGenericIntentUrl("");
    setIosUpiApps([]);
    setIosChooserVisible(false);
    setShowProcessingModal(false);
    setCountdownSeconds(null);
    setTimerExpired(false);
    setManualCanceling(false);
    setShowCancelConfirm(false);
  }, [stopAll]);

  const openPendingModal = useCallback((label) => {
    if (finishedRef.current) return;
    setLoading(true);
    setShowProcessingModal(true);
    setStatusLabel(label || "Waiting for payment confirmation...");
  }, []);

  const openCancelConfirm = useCallback(() => {
    if (finishedRef.current) return;
    setShowCancelConfirm(true);
  }, []);

  const redirectToTarget = useCallback(
    (target) => {
      if (!target) {
        navigate("/booking-ticket");
        return;
      }
      if (isExternalUrl(target)) {
        window.location.replace(target);
        return;
      }
      navigate(target);
    },
    [navigate]
  );

  const cancelBookingTransaction = useCallback(
    async ({ redirectHome = true } = {}) => {
      if (manualCanceling || cancellingBooking) return;

      setManualCanceling(true);
      setLoading(true);

      try {
        await axios.post(
          `${appConstants.base_url}v1/booking/ticketing/cancel`,
          {
            qr: "YES",
            bookType: localStorage.getItem("booking_type"),
            transId: localStorage.getItem("transid"),
            bookingId,
            ccode: localStorage.getItem("theaterId"),
          },
          {
            headers: {
              chain,
              city: localStorage.getItem("city"),
              Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
              appVersion: appConstants.av,
              platform: appConstants.pt,
              country: appConstants.cname,
            },
          }
        );
      } catch {
      } finally {
        stopAll();
        clearActivePayment();
        finishedRef.current = true;
        setLoading(false);
        setShowProcessingModal(false);
        setIosChooserVisible(false);
        setCountdownSeconds(null);
        setTimerExpired(false);
        setStatusLabel("Payment cancelled.");
        setShowCancelConfirm(false);

        if (redirectHome) {
          setCancellingBooking(true);
          setTimeout(() => {
            setCancellingBooking(false);
            navigate("/");
          }, REDIRECT_DELAY_MS);
        } else {
          setManualCanceling(false);
          navigate("/");
        }
      }
    },
    [bookingId, chain, cancellingBooking, manualCanceling, navigate, stopAll]
  );

  const handleFailure = useCallback(
    (message) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      stopAll();
      clearActivePayment();
      setLoading(false);
      setShowProcessingModal(false);
      setIosChooserVisible(false);
      setCountdownSeconds(null);
      setStatusLabel("Payment was not completed.");
      setAlertState({ show: true, title: "Payment Failed", message });
    },
    [stopAll]
  );

  const handleSuccess = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;

    const target = forwardUrlRef.current || "/booking-ticket";

    stopAll();
    clearActivePayment();
    localStorage.setItem("booking_ticket", 1);

    setLoading(false);
    setShowProcessingModal(false);
    setIosChooserVisible(false);
    setCountdownSeconds(null);
    setTimerExpired(false);
    setStatusLabel("Payment successful. Redirecting...");

    setTimeout(() => {
      redirectToTarget(target);
    }, 250);
  }, [redirectToTarget, stopAll]);

  const handleTimeout = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    stopAll();
    clearActivePayment();
    setLoading(false);
    setShowProcessingModal(false);
    setIosChooserVisible(false);
    setStatusLabel("Session expired. Cancelling your booking...");
    cancelBookingTransaction();
  }, [cancelBookingTransaction, stopAll]);

  const checkPaymentStatus = useCallback(async () => {
    if (!paymentIdRef.current || inFlightRef.current || finishedRef.current) return;

    inFlightRef.current = true;

    try {
      const res = await axios.post(
        `${appConstants.base_url}v1/payment/juspay/status`,
        {
          bookingId,
          paymentId: paymentIdRef.current,
          bookType: getBookType(),
        },
        { headers: headersRef.current }
      );

      const apiStatus = String(res?.data?.output?.p || "").toUpperCase();

      if (res?.data?.result !== "success") {
        openPendingModal("Checking payment status...");
        return;
      }

      if (SUCCESS_STATUSES.includes(apiStatus)) {
        handleSuccess();
        return;
      }

      if (PENDING_STATUSES.includes(apiStatus)) {
        openPendingModal("Waiting for payment confirmation...");
        return;
      }

      handleFailure(`Payment ${apiStatus?.replace(/_/g, " ") || "Failed"}`);
    } catch {
      openPendingModal("Rechecking payment status...");
    } finally {
      inFlightRef.current = false;
    }
  }, [bookingId, handleFailure, handleSuccess, openPendingModal]);

  const startCountdown = useCallback(() => {
    clearCountdownTimer();
    setTimerExpired(false);
    setCountdownSeconds(PAYMENT_TIMEOUT_SECONDS);

    countdownTimerRef.current = setInterval(() => {
      setCountdownSeconds((prev) => {
        if (prev <= 1) {
          clearCountdownTimer();
          setTimerExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const startPolling = useCallback(() => {
    clearPollTimer();
    checkPaymentStatus();
    pollTimerRef.current = setInterval(() => {
      checkPaymentStatus();
    }, STATUS_INTERVAL);
  }, [checkPaymentStatus]);

  const beginProcessing = useCallback(() => {
    setShowProcessingModal(true);
    setLoading(true);
    setIosChooserVisible(false);
    setStatusLabel("Waiting for payment confirmation...");
    startCountdown();
    startPolling();
  }, [startCountdown, startPolling]);

  const launchIntent = useCallback(
    (intentUrl) => {
      if (!intentUrl) {
        setAlertState({ show: true, title: "Payment Error", message: "UPI intent details are missing." });
        navigate("/");
        return;
      }
      beginProcessing();
      window.location.assign(intentUrl);
    },
    [beginProcessing, navigate]
  );

  const handlePayment = async () => {
    try {
      if (!isMobile) throw new Error("Please open this on a mobile device for UPI payment.");

      resetFlow();
      setLoading(true);
      setAlertState({ show: false, title: "", message: "" });
      setStatusLabel("Preparing your secure UPI payment...");

      const response = await axios.post(
        `${appConstants.base_url}v1/payment/juspay/hmac`,
        {
          transId,
          bookingId,
          bookType,
          paytype: "UPI",
          pgtype: "JUSPAY",
          mandate: localStorage.getItem("booking_type") === "PASSPORT" && subscription === "true",
        },
        { headers: headersRef.current }
      );

      const data = response?.data;
      if (data?.result !== "success") {
        throw new Error(data?.error || data?.msg || "Failed to initialize payment.");
      }

      const out = data?.output || {};
      const paymentId = out.paymentId || out.bookingId;
      const nextIntentUrl = out.generic_intent;
      const nextUpiApps = Array.isArray(out?.upiApps) ? out.upiApps : [];

      paymentIdRef.current = paymentId;
      forwardUrlRef.current = out.forwardUrl;
      setGenericIntentUrl(nextIntentUrl);
      setIosUpiApps(nextUpiApps);
      localStorage.setItem("pgused", "UPI");
      localStorage.setItem("paymentid", paymentId);
      setRetryPayment(payMode, `retry-UPI${bookingId}`);
      persistActivePayment({ bookingId, paymentId, forwardUrl: out.forwardUrl });

      if (out.amount) setAmount(Number(out.amount));

      setLoading(false);

      if (isIOS) {
        setIosChooserVisible(true);
        setStatusLabel("Choose your preferred UPI app to continue.");
        return;
      }

      if (nextIntentUrl) {
        launchIntent(nextIntentUrl);
        return;
      }

      throw new Error("UPI intent not available.");
    } catch (err) {
      setLoading(false);
      setAlertState({
        show: true,
        title: "Payment Error",
        message: err?.message || "Payment failed. Please try again.",
      });
    }
  };

  useEffect(() => {
    if (!timerExpired) return;
    handleTimeout();
  }, [handleTimeout, timerExpired]);

  useEffect(() => {
    const activePayment = getStoredActivePayment();

    if (activePayment?.paymentId && activePayment?.bookingId === bookingId && !reloadCancelDoneRef.current) {
      reloadCancelDoneRef.current = true;
      cancelBookingTransaction({ redirectHome: false });
      return;
    }

    if (activePayment?.paymentId && activePayment?.bookingId === bookingId) {
      paymentIdRef.current = activePayment.paymentId;
      forwardUrlRef.current = activePayment.forwardUrl || "";
      beginProcessing();
    }

    const handleVisibilityChange = () => {
      if (!document.hidden && paymentIdRef.current && !finishedRef.current) {
        openPendingModal("Checking your payment status...");
        startPolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopAll();
    };
  }, [bookingId, beginProcessing, cancelBookingTransaction, openPendingModal, startPolling, stopAll]);

  useEffect(() => {
    if (!showProcessingModal) return;

    const onBeforeUnload = () => {
      if (paymentIdRef.current && !finishedRef.current) {
        sessionStorage.setItem(
          ACTIVE_UPI_PAYMENT_KEY,
          JSON.stringify({
            bookingId,
            paymentId: paymentIdRef.current,
            forwardUrl: forwardUrlRef.current || "",
          })
        );
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [bookingId, showProcessingModal]);

  const isPayNowDisabled = loading || showProcessingModal || timerExpired || cancellingBooking;
  const progressWidth =
    countdownSeconds !== null ? `${(countdownSeconds / PAYMENT_TIMEOUT_SECONDS) * 100}%` : "100%";

  if (cancellingBooking) {
    return (
      <div className="upi-cancel-screen">
        <BookingSummarySkeleton />
        <p className="upi-cancel-text">Session expired. Cancelling booking and redirecting to home…</p>
      </div>
    );
  }

  return (
    <>
      <SweetAlert
        error={alertState.title !== "Payment In Progress"}
        warning={alertState.title === "Payment In Progress"}
        show={alertState.show}
        confirmBtnCssClass={theme === "inoxTheme" ? "confirm-btn-inox border-0" : "confirm-btn-pvr border-0"}
        title={alertState.title}
        onConfirm={() => setAlertState({ show: false, title: "", message: "" })}
      >
        {alertState.message}
      </SweetAlert>

      <ConfirmCancelModal
        open={showCancelConfirm}
        loading={manualCanceling}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => cancelBookingTransaction({ redirectHome: true })}
      />

      {showProcessingModal && (
        <div className="upi-modal-backdrop">
          <div className="upi-modal">
            <div className="upi-modal-header">
              <div className="upi-loader-ring-wrap">
                <div className="upi-loader-ring" />
                <div className="upi-loader-initial">UPI</div>
              </div>
              <div className="upi-modal-header-copy">
                <div className="upi-modal-title">Payment Processing</div>
                <div className="upi-modal-desc">
                  Complete the payment in your UPI app and return to this screen automatically.
                </div>
              </div>
            </div>

            <div className="upi-processing-card">
              <div className="upi-processing-row">
                <div className="upi-processing-left">
                  <div className="upi-small-label">Amount to pay</div>
                  <div className="upi-processing-amount">
                    {amount !== null ? `₹${Number(amount).toFixed(2)}` : "--"}
                  </div>
                </div>

                <div className="upi-timer-circle">
                  <div className="upi-timer-value">
                    {countdownSeconds === null
                      ? "0:00"
                      : `${Math.floor(countdownSeconds / 60)}:${String(countdownSeconds % 60).padStart(2, "0")}`}
                  </div>
                  <div className="upi-timer-text">TIME LEFT</div>
                </div>
              </div>

              <div className="upi-progress-shell">
                <div className="upi-progress-bar" style={{ width: progressWidth }} />
              </div>

              <div className="upi-note">
                This session will auto-expire in 2 minutes if payment is not confirmed.
              </div>
            </div>

            <div className="upi-status-box">
              <div className="upi-status-title">Current status</div>
              <div className="upi-status-text">{statusLabel}</div>
            </div>

            <div className="upi-warning-box">
              <div className="upi-warning-title">Important</div>
              <div className="upi-warning-text">
                Do not press back or refresh this page while payment is in progress. Your session may expire and the booking can be cancelled.
              </div>
            </div>

            <div className="upi-modal-actions">
              <button type="button" className="upi-cancel-btn" onClick={openCancelConfirm}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="upi-page">
        <div className="upi-card">
          <div className="upi-card-head">
            <div className="upi-card-copy">
              <div className="upi-card-title">UPI Payment</div>
              <div className="upi-card-subtitle">Secure and instant payment using your preferred UPI app</div>
            </div>

            <div className="upi-payable-box">
              <div className="upi-payable-label">Payable</div>
              <div className="upi-payable-amount">{amount !== null ? formatAmount(amount) : "--"}</div>
            </div>
          </div>

          <div className="upi-main-message">{statusLabel}</div>
          {!showProcessingModal && (
            <div className="upi-warning-inline mt-3">
              <div className="upi-warning-title">Do not go back or refresh</div>
              <div className="upi-warning-text">
                Once payment starts, please stay on this screen until confirmation is complete.
              </div>
            </div>
          )}

          {iosChooserVisible && !showProcessingModal && (
            <div className="upi-app-section">
              <div className="upi-app-title">Choose UPI app</div>
              <div className="upi-app-subtitle">Select your preferred app to continue payment.</div>
              <div className="upi-app-grid">
                {iosUpiApps.map((app) => (
                  <button
                    key={app.id}
                    type="button"
                    onClick={() => {
                      const iosIntent = buildIosIntentUrl(app.handle, genericIntentUrl);
                      launchIntent(iosIntent);
                    }}
                    className="upi-app-btn"
                  >
                      {/* {!!app.logo && (
                      <img
                        src={app.logo}
                        alt={app.name}
                        className="upi-app-logo"
                      />
                    )} */}
                    {app.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="upi-footer">
        <div className="upi-footer-inner">
          <ProceedButton onClick={handlePayment} disabled={isPayNowDisabled} className="upi-pay-btn">
            {loading
              ? "Processing…"
              : timerExpired
              ? "Session Expired"
              : iosChooserVisible
              ? "Select UPI App Above"
              : showProcessingModal
              ? "Payment in progress…"
              : "Pay Now"}
          </ProceedButton>
        </div>
      </div>
    </>
  );
};

export default Upi;